import React, { useRef, useState } from "react";
import { DropdownDivider, DropdownHeader, DropdownItem, Icon } from "@axum/ax-design";
import { useClassicView } from "../../../config/classicView";
import { DEFAULT_VIEW_SETTINGS, isDefaultView, ViewSettings } from "../../../config/viewSettings";
import { HeaderMenuAction } from "./HeaderActions";
import { ViewMenuButton, ViewOptionsMenu, ViewSettingsMenu } from "./styles";
import { SPLIT_MEDIA } from "../../Common/Orders/styles";

interface Props {
  settings: ViewSettings;
  onChange: (settings: ViewSettings) => void;
  actions: { id: string; action: HeaderMenuAction }[];
}

interface SwitchItemProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: React.ReactNode;
}

const SwitchItem = (props: SwitchItemProps) => (
  <button
    type="button"
    role="menuitemcheckbox"
    aria-checked={props.checked}
    tabIndex={-1}
    className="ax-dropdown-item o360-view-setting"
    onClick={() => props.onChange(!props.checked)}
  >
    {props.children}
    <span className="o360-switch" aria-hidden="true" />
  </button>
);

type OpenMenu = "options" | "settings" | null;

export default (props: Props) => {
  const { setClassic } = useClassicView();
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState<OpenMenu>(null);
  const { settings } = props;
  const change = (patch: Partial<ViewSettings>) => props.onChange({ ...settings, ...patch });
  const close = () => setOpen(null);

  const sideButton = (end: boolean, label: string) => {
    const active = settings.filtersEnd === end;
    return (
      <button
        type="button"
        role="menuitemcheckbox"
        aria-checked={active}
        tabIndex={-1}
        disabled={settings.floatingFilters}
        className={`ax-segmented-btn${active ? " is-active" : ""}`}
        onClick={() => change({ filtersEnd: end })}
      >
        {label}
      </button>
    );
  };

  return (
    <>
      <ViewMenuButton
        ref={anchor}
        label="Más opciones"
        aria-haspopup="menu"
        aria-expanded={open !== null}
        className={isDefaultView(settings) ? undefined : "has-changes"}
        data-cy="ViewMenu"
        onClick={() => setOpen(current => (current ? null : "options"))}
      >
        <Icon name="more" />
      </ViewMenuButton>
      <ViewOptionsMenu open={open === "options"} anchor={anchor} onClose={close} align="end">
        {props.actions.map(({ id, action }) => (
          <DropdownItem key={id} icon={action.icon} className={action.className} disabled={action.disabled} onSelect={action.onSelect}>
            {action.label}
          </DropdownItem>
        ))}
        {props.actions.length > 0 && <DropdownDivider />}
        <DropdownItem icon={<Icon name="settings" />} onSelect={() => setOpen("settings")} data-cy="EditarPantalla">
          Editar pantalla
        </DropdownItem>
        <DropdownItem icon={<Icon name="list" />} onSelect={() => setClassic(true)} data-cy="VistaClasica">
          Vista clásica
        </DropdownItem>
      </ViewOptionsMenu>
      <ViewSettingsMenu open={open === "settings"} anchor={anchor} onClose={close} align="end" aria-label="Editar pantalla">
        <DropdownHeader>Editar pantalla</DropdownHeader>
        <SwitchItem checked={settings.kpis} onChange={kpis => change({ kpis })}>Indicadores</SwitchItem>
        {!window.matchMedia(SPLIT_MEDIA).matches && (
          <>
            <SwitchItem checked={!settings.floatingFilters} onChange={column => change({ floatingFilters: !column })}>
              Panel de filtros
            </SwitchItem>
            <div className="o360-view-setting o360-view-setting-row" aria-disabled={settings.floatingFilters}>
              Posición
              <span className="ax-segmented">
                {sideButton(false, "Izquierda")}
                {sideButton(true, "Derecha")}
              </span>
            </div>
          </>
        )}
        <DropdownDivider />
        <DropdownItem
          className="o360-view-reset"
          onClick={event => {
            event.preventDefault();
            props.onChange(DEFAULT_VIEW_SETTINGS);
          }}
        >
          Restablecer
        </DropdownItem>
      </ViewSettingsMenu>
    </>
  );
};
